import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { getCityPageBySlug } from "@/data/citiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison Nanterre + 2 features + 3 engagements ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison intégrée
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Vos fournitures de déménagement livrées à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Oubliez les pertes de temps dans les zones commerciales
            périphériques et la corvée de transporter des liasses
            d&apos;emballages volumineuses. Nous prenons en main tout le
            circuit de transport jusqu&apos;à votre adresse à {city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Filière de transport 100 % intégrée",
              body: "C'est notre propre société francilienne qui assure la livraison, sans aucun intermédiaire. Nous maîtrisons la chaîne de A à Z, ce qui garantit un soin extrême lors du portage et une ponctualité rigoureuse.",
              Icon: Truck,
            },
            {
              title: "Intégrité matérielle totale",
              body: "Voyageant à l'abri des intempéries dans nos fourgons fermés, vos cartons conservent leur raideur d'usine. Aucun risque de recevoir du matériel ramolli par l'humidité : tout est prêt à être assemblé et scellé.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <h3 className="mt-10 font-display font-extrabold text-lg md:text-xl text-[#0f1d2b]">
          Nos engagements envers vous
        </h3>
        <p className="mt-2 text-[14px] text-[#5b6b7a] max-w-3xl">
          Pour vous garantir une expérience d&apos;achat en toute sérénité,
          notre service repose sur trois engagements concrets.
        </p>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Zéro rupture de stock",
              body: "Grâce à notre plateforme logistique régionale, nous maintenons une disponibilité permanente sur toutes nos références pour répondre à vos urgences.",
              Icon: Boxes,
            },
            {
              title: "Calibre industriel",
              body: "Nous refusons le carton d'entrée de gamme. Tous nos modèles affichent une résistance au gerbage (l'empilement vertical) certifiée pour éviter tout écrasement dans le camion.",
              Icon: Layers,
            },
            {
              title: "Éco-responsabilité",
              body: "Nos fournitures intègrent des fibres d'origine contrôlée et sont totalement recyclables, vous permettant de limiter l'empreinte environnementale de votre changement d'adresse.",
              Icon: Recycle,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h4 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h4>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — L'art du classement (3 cartons cliquables) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Le bon contenant
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            L&apos;art du classement : à chaque carton sa spécialité
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour optimiser le chargement et éviter la casse, chaque type
            d&apos;objet nécessite un contenant adapté à sa densité. Découvrez
            notre gamme technique.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              slug: "carton-standard-renforce",
              title: "Le module Standard",
              body: "Le couteau suisse du déménagement. Ses dimensions sont idéales pour le linge, le petit électroménager, les jouets et les objets du quotidien.",
              Icon: Package,
            },
            {
              slug: "carton-a-livres",
              title: "Le module Livres & Documents",
              body: "Plus compact et ultra-robuste, il est conçu spécifiquement pour supporter les charges lourdes (archives, romans, revues) sans ne se déformer ni vous briser le dos au levage.",
              Icon: ShieldCheck,
            },
            {
              slug: "carton-penderie-grand",
              title: "Le module Penderie",
              body: "Une véritable armoire mobile en carton munie d'une tringle métallique. Il vous permet de transférer vos costumes, robes et vestes directement depuis votre dressing sans les plier ni les froisser.",
              Icon: Layers,
            },
          ].map(({ slug, title, body, Icon }) => (
            <Link
              key={slug}
              to={`/produit/${slug}`}
              className="group bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 block"
              data-testid={`city-nanterre-carton-${slug}`}
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white shrink-0">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#237cb5] opacity-0 group-hover:opacity-100 transition-opacity">
                Voir ce carton <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 4 — Protection et enveloppement ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Protection &amp; enveloppement
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Découvrez nos produits de protection et d&apos;enveloppement
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que l&apos;enveloppe extérieure ne suffit pas toujours, nous
            mettons à votre disposition un arsenal complet pour isoler vos
            affaires des vibrations de la route.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              slug: "papier-bulle-1x100m",
              title: "Le film bulle alvéolé",
              body: "Sa structure emprisonne l'air pour former un amortisseur continu autour de votre vaisselle, de vos miroirs ou de vos écrans.",
              Icon: Sparkles,
            },
            {
              slug: "housse-matelas-2-places",
              title: "Les housses hermétiques",
              body: "Des protections en polyéthylène épais spécialement dimensionnées pour envelopper vos matelas et canapés, les isolant de la poussière et des frottements pendant la manutention.",
              Icon: ShieldCheck,
            },
            {
              slug: "papier-blanc-journal",
              title: "Le papier de calage kraft",
              body: "La solution idéale pour combler les espaces vides à l'intérieur de vos cartons, bloquer le contenu et empêcher les objets de s'entrechoquer durant les virages.",
              Icon: Layers,
            },
          ].map(({ slug, title, body, Icon }) => (
            <Link
              key={slug}
              to={`/produit/${slug}`}
              className="group bg-white rounded-2xl border border-[#cfdbe6] p-5 hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 block"
              data-testid={`city-nanterre-protection-${slug}`}
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white shrink-0">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#237cb5] opacity-0 group-hover:opacity-100 transition-opacity">
                Voir le produit <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-nanterre-cta-emballage"
          >
            <ShieldCheck className="h-4 w-4" strokeWidth={2.6} />
            Toutes les protections et emballages
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Livraison partout dans le 92 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Couverture {city.deptCode}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Vos cartons et fournitures livrés partout dans les
            {" "}{city.deptName} ({city.deptCode})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Vous quittez notre secteur immédiat ou vous restez branché sur le
            département&nbsp;? Nos camions de livraison et nos équipes de
            déménagement sillonnent chaque jour les axes du {city.deptCode}
            {" "}pour acheminer votre matériel de protection et encadrer vos
            projets de A à Z. Découvrez les communes voisines où nos services
            sont les plus sollicités&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement/${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-nanterre-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
